import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';

import { PageHeader, Form, Input, Button, Cascader, Spin, message, DatePicker, InputNumber, Select, Space } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import ColorHash from 'color-hash';

import * as ToolRequest from '../../tools/ToolRequest';

import SpinFull from '../../components/SpinFull';
import CropNameBadge from '../../components/CropNameBadge';

const colorHash = new ColorHash();

const ZoneInput = ({ value, onChange, style, ...props }) => (
  <Input
    {...props}
    value={value}
    onChange={onChange}
    style={{
      width: 100,
      ...style,
      ...(value ? { backgroundColor: colorHash.hex(value) } : null),
    }}
  />
);

const formLayout = {
  labelCol: {
    sm: { span: 5 },
  },
  wrapperCol: {
    sm: { span: 16 },
  },
  labelWrap: true,
};

const formTailLayout = {
  wrapperCol: {
    sm: { offset: 5, span: 16 },
  },
};

const { Option } = Select;

const sourceOptions = [
  { value: 'LOCAL', label: 'Local' },
  { value: 'EXTERNAL', label: 'External' },
];

const stageOptions = [
  { value: 'PROPAGATION', label: 'Propagation' },
  { value: 'PRODUCTION', label: 'Production' },
];

const getDefaultEntry = (type) => {
  if (type === 'WASTE') {
    return { stage: 'PROPAGATION' };
  }
  if (type === 'SOW' || type === 'TRANSPLANT') {
    return { source: 'LOCAL' };
  }
  return {};
};

export default function FarmProductionRecordAddForm({ type, title, successMessage }) {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const presetSiteId = searchParams.get('siteId');
  const presetCropId = searchParams.get('cropId');
  const presetAmount = searchParams.get('amount');
  const presetRecordedAt = searchParams.get('recordedAt') || searchParams.get('date');
  const presetStage = searchParams.get('stage');
  const presetEntriesRaw = searchParams.get('entries');
  const [form] = Form.useForm();

  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [siteOptions, setSiteOptions] = useState([]);
  const [crops, setCrops] = useState([]);
  const [initialSiteId, setInitialSiteId] = useState(null);

  const getPresetEntries = () => {
    if (presetEntriesRaw) {
      try {
        const parsed = JSON.parse(presetEntriesRaw);
        if (Array.isArray(parsed) && parsed.length) {
          return parsed.map(entry => ({
            ...getDefaultEntry(type),
            cropId: entry.cropId,
            amount: entry.amount != null ? Number(entry.amount) : undefined,
            ...(type === 'WASTE' && entry.stage ? { stage: entry.stage } : {}),
          }));
        }
      } catch (err) {
        // fall through to single-entry presets
      }
    }
    return [{
      ...getDefaultEntry(type),
      ...(presetCropId ? { cropId: presetCropId } : {}),
      ...(presetAmount ? { amount: Number(presetAmount) } : {}),
      ...(type === 'WASTE' && presetStage ? { stage: presetStage } : {}),
    }];
  };

  const loadCrops = async (companyId) => {
    const cropsRes = await ToolRequest.request('GET', '/v1/farm-crop', {
      companyId,
      isEnabled: true,
      count: 1000,
    });
    setCrops(cropsRes.data.sort((a, b) => a.name.localeCompare(b.name)));
  };

  useEffect(() => {
    (async () => {
      const sitesRes = await ToolRequest.request('GET', '/v1/farm-site', {
        count: 1000,
      });

      const options = sitesRes.data.map(site => ({
        value: site.id,
        label: site.name,
        companyId: site.companyId,
      }));
      setSiteOptions(options);

      if (presetSiteId) {
        const site = options.find(s => s.value === presetSiteId);
        if (site) {
          setInitialSiteId([presetSiteId]);
          await loadCrops(site.companyId);
        }
      }

      setLoaded(true);
    })();
  }, []);

  const onChangeSite = async (siteId) => {
    const site = siteOptions.find(s => s.value === siteId);
    form.setFieldsValue({ entries: [getDefaultEntry(type)] });
    if (!site) {
      setCrops([]);
      return;
    }
    await loadCrops(site.companyId);
  };

  const onFinish = async (values) => {
    setLoading(true);

    try {
      const siteId = values.siteId[0];
      const recordedAt = moment(values.recordedAt).toISOString();
      const entries = values.entries.filter(entry => entry && entry.cropId && entry.amount);

      for (let entry of entries) {
        const data = {
          siteId,
          cropId: entry.cropId,
          type,
          amount: entry.amount,
          zone: entry.zone,
          recordedAt,
          remarks: values.remarks,
        };

        if (type === 'HARVEST' && entry.weight) {
          data.weight = entry.weight;
        }

        if (type === 'SOW' || type === 'TRANSPLANT') {
          data.source = entry.source || 'LOCAL';
        }

        if (type === 'WASTE') {
          data.stage = entry.stage;
        }

        await ToolRequest.request('POST', '/v1/farm-production-record', data);
      }

      message.success(entries.length > 1 ? `${entries.length} records created successfully` : successMessage);

      history.push('/farm-production-record');
    } catch (err) {
      setLoading(false);

      if (err?.response?.data) {
        switch (err.response.data) {
          default:
            return message.error('Unknown errors. Please retry');
        }
      }

      message.error('Unknown error. Please retry');
    }
  };

  return loaded ? (
    <PageHeader
      title={title}
      onBack={() => history.push('/farm-production-record')}
    >
      <Spin spinning={loading}>
        <Form
          {...formLayout}
          form={form}
          initialValues={{
            recordedAt: presetRecordedAt ? moment(presetRecordedAt) : moment(),
            entries: getPresetEntries(),
            ...(initialSiteId ? { siteId: initialSiteId } : {}),
          }}
          onFinish={onFinish}
        >
          <Form.Item
            label="Site"
            name="siteId"
            rules={[
              {
                required: true,
                message: 'Please select a site',
              },
            ]}
          >
            <Cascader
              options={siteOptions}
              onChange={values => onChangeSite(values[0])}
              placeholder="Select Site"
              allowClear={false}
            />
          </Form.Item>

          <Form.Item
            label="Recorded At"
            name="recordedAt"
            rules={[
              {
                required: true,
                message: 'Please select the recorded date',
              },
            ]}
          >
            <DatePicker showTime />
          </Form.Item>

          <Form.List
            name="entries"
            rules={[
              {
                validator: async (_, entries) => {
                  if (!entries || !entries.length) {
                    throw new Error('Add at least one crop');
                  }
                  const filled = entries.filter(entry => {
                    if (!entry || !entry.cropId || !entry.amount) {
                      return false;
                    }
                    if (type === 'WASTE') {
                      return !!entry.stage;
                    }
                    return true;
                  });
                  if (!filled.length) {
                    throw new Error('Add at least one crop with amount');
                  }
                  const partial = entries.some(entry => {
                    if (!entry) {
                      return false;
                    }
                    const hasAny = entry.cropId || entry.amount || entry.zone || entry.weight
                      || (entry.source && entry.source !== 'LOCAL')
                      || (entry.stage && entry.stage !== 'PROPAGATION');
                    const hasAll = entry.cropId && entry.amount && (type !== 'WASTE' || entry.stage);
                    return hasAny && !hasAll;
                  });
                  if (partial) {
                    throw new Error('Complete or remove incomplete rows');
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map((field, index) => (
                  <Form.Item
                    label={index === 0 ? 'Crops' : ' '}
                    colon={index === 0}
                    required={index === 0}
                    key={field.key}
                  >
                    <Space align="baseline" wrap>
                      <Form.Item
                        {...field}
                        name={[field.name, 'cropId']}
                        noStyle
                      >
                        <Select
                          placeholder="Crop"
                          style={{ width: 160 }}
                          disabled={!crops.length}
                        >
                          {crops.map(crop => (
                            <Option key={crop.id} value={crop.id}>
                              <CropNameBadge name={crop.name} />
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, 'amount']}
                        noStyle
                      >
                        <InputNumber placeholder="Amount" min={1} style={{ width: 100 }} />
                      </Form.Item>
                      {type === 'HARVEST' && (
                        <Form.Item
                          {...field}
                          name={[field.name, 'weight']}
                          noStyle
                        >
                          <InputNumber placeholder="Weight (g)" min={1} style={{ width: 110 }} />
                        </Form.Item>
                      )}
                      {(type === 'SOW' || type === 'TRANSPLANT') && (
                        <Form.Item
                          {...field}
                          name={[field.name, 'source']}
                          initialValue="LOCAL"
                          noStyle
                        >
                          <Select placeholder="Source" style={{ width: 110 }}>
                            {sourceOptions.map(option => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      )}
                      {type === 'WASTE' && (
                        <Form.Item
                          {...field}
                          name={[field.name, 'stage']}
                          initialValue="PROPAGATION"
                          noStyle
                        >
                          <Select placeholder="Stage" style={{ width: 120 }}>
                            {stageOptions.map(option => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      )}
                      <Form.Item
                        {...field}
                        name={[field.name, 'zone']}
                        noStyle
                      >
                        <ZoneInput placeholder="Zone" />
                      </Form.Item>
                      {fields.length > 1 && (
                        <MinusCircleOutlined onClick={() => remove(field.name)} />
                      )}
                    </Space>
                  </Form.Item>
                ))}
                <Form.Item {...formTailLayout}>
                  <Button
                    type="dashed"
                    onClick={() => add(getDefaultEntry(type))}
                    block
                    icon={<PlusOutlined />}
                    disabled={!crops.length}
                  >
                    Add crop
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item
            label="Remarks"
            name="remarks"
          >
            <Input.TextArea
              placeholder="Remarks"
            />
          </Form.Item>

          <Form.Item {...formTailLayout}>
            <Button type="primary" htmlType="submit" block>
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </PageHeader>
  ) : <SpinFull />;
}
